import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, Building, Coins, TrendingUp, HelpCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import CustomSelect from '../../../components/ui/CustomSelect';
import Tooltip from '../../../components/ui/Tooltip';

export default function CategorielRevenus({ data, onChange, calculations }) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const handleFieldChange = (field, val) => {
    onChange(prev => ({ ...prev, [field]: val }));
  };

  const isCouple = data.statut === "marie" || data.statut === "pacse";

  return (
    <div className="space-y-6">
      {/* 1. Traitements, Salaires & Rémunérations */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center justify-between pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Traitements, Salaires & Pensions</h3>
              <p className="text-xs text-subtle">Revenus d'activité professionnelle nette imposable.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-subtle font-medium">Déduction frais :</label>
            <div className="flex rounded-lg border border-workspace p-0.5 bg-surface-muted/40">
              <button
                type="button"
                onClick={() => handleFieldChange("typeFrais", "forfait")}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${data.typeFrais === 'forfait' ? 'bg-surface text-primary shadow-sm' : 'text-subtle hover:text-primary'}`}
              >
                Forfaitaire (10%)
              </button>
              <button
                type="button"
                onClick={() => handleFieldChange("typeFrais", "reel")}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${data.typeFrais === 'reel' ? 'bg-surface text-primary shadow-sm' : 'text-subtle hover:text-primary'}`}
              >
                Frais Réels
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Salaire Déclarant 1 (€ / an)</label>
            <Input
              type="number"
              min="0"
              value={data.salaireDeclarant1 || ''}
              onChange={(e) => handleFieldChange("salaireDeclarant1", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
              className="text-base font-bold text-primary"
            />
            {data.typeFrais === 'reel' && (
              <div className="mt-2">
                <label className="block text-[11px] text-subtle mb-1">Montant frais réels Déclarant 1 (€)</label>
                <Input
                  type="number"
                  min="0"
                  value={data.fraisReelsDeclarant1 || ''}
                  onChange={(e) => handleFieldChange("fraisReelsDeclarant1", Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="0"
                />
              </div>
            )}
          </div>

          {isCouple && (
            <div>
              <label className="block text-xs font-medium text-subtle mb-1.5">Salaire Déclarant 2 (Conjoint) (€ / an)</label>
              <Input
                type="number"
                min="0"
                value={data.salaireDeclarant2 || ''}
                onChange={(e) => handleFieldChange("salaireDeclarant2", Math.max(0, parseInt(e.target.value) || 0))}
                placeholder="0"
                className="text-base font-bold text-primary"
              />
              {data.typeFrais === 'reel' && (
                <div className="mt-2">
                  <label className="block text-[11px] text-subtle mb-1">Montant frais réels Déclarant 2 (€)</label>
                  <Input
                    type="number"
                    min="0"
                    value={data.fraisReelsDeclarant2 || ''}
                    onChange={(e) => handleFieldChange("fraisReelsDeclarant2", Math.max(0, parseInt(e.target.value) || 0))}
                    placeholder="0"
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">BNC / BIC / Rémunération Gérance Art. 62 (€ / an)</label>
            <Input
              type="number"
              min="0"
              value={data.bicBncPro || ''}
              onChange={(e) => handleFieldChange("bicBncPro", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Pensions de Retraite / Rentes Viagères (€ / an)</label>
            <Input
              type="number"
              min="0"
              value={data.pensionsRetraite || ''}
              onChange={(e) => handleFieldChange("pensionsRetraite", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
            />
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-workspace flex justify-between text-xs text-subtle">
          <span>Net imposable après abattement des salaires :</span>
          <strong className="text-primary">{formatEuro(calculations.salairesNetImposable)}</strong>
        </div>
      </div>

      {/* 2. Revenus Fonciers (Micro vs Réel & Déficit) */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Building className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Revenus Fonciers (Immobilier Locatif Nu)</h3>
              <p className="text-xs text-subtle">Micro-foncier (abattement 30% si &lt; 15 000 €) ou Régime Réel déductible.</p>
            </div>
          </div>
          <div className="flex rounded-lg border border-workspace p-0.5 bg-surface-muted/40">
            <button
              type="button"
              onClick={() => handleFieldChange("regimeFoncier", "micro")}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${data.regimeFoncier === 'micro' ? 'bg-surface text-primary shadow-sm' : 'text-subtle hover:text-primary'}`}
            >
              Micro-Foncier (30%)
            </button>
            <button
              type="button"
              onClick={() => handleFieldChange("regimeFoncier", "reel")}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${data.regimeFoncier === 'reel' ? 'bg-surface text-primary shadow-sm' : 'text-subtle hover:text-primary'}`}
            >
              Régime Réel
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Loyers Bruts Encaissés (€)</label>
            <Input
              type="number"
              min="0"
              value={data.loyersBruts || ''}
              onChange={(e) => handleFieldChange("loyersBruts", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
            />
          </div>

          {data.regimeFoncier === 'reel' && (
            <>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1.5">Charges, Gestion & Assurances (€)</label>
                <Input
                  type="number"
                  min="0"
                  value={data.chargesDeductiblesFoncier || ''}
                  onChange={(e) => handleFieldChange("chargesDeductiblesFoncier", Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1.5">Intérêts d'Emprunt Foncier (€)</label>
                <Input
                  type="number"
                  min="0"
                  value={data.interetsEmpruntFoncier || ''}
                  onChange={(e) => handleFieldChange("interetsEmpruntFoncier", Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-subtle mb-1.5">Travaux d'Entretien / Rénovation (€)</label>
                <Input
                  type="number"
                  min="0"
                  value={data.travauxFoncier || ''}
                  onChange={(e) => handleFieldChange("travauxFoncier", Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="0"
                />
              </div>
            </>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-workspace flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-2">
          {calculations.deficitFoncierImputable > 0 ? (
            <span className="text-success-600 font-bold">
              💡 Déficit foncier déductible du revenu global : -{formatEuro(calculations.deficitFoncierImputable)}
            </span>
          ) : (
            <span className="text-subtle">
              Revenu foncier net soumis au barème : <strong className="text-primary">{formatEuro(calculations.revenuFoncierImposable)}</strong>
              {" "}(+ {formatEuro(calculations.prelevementsSociauxFoncier)} de Prélèvements Sociaux 17.2%)
            </span>
          )}
        </div>
      </div>

      {/* 3. Dividendes & Comparateur PFU vs Barème */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Dividendes & RCM (Arbitrage Fiscal PFU vs Barème)</h3>
              <p className="text-xs text-subtle">Comparateur d'optimisation entre Flat Tax (30%) et Barème progressif avec abattement de 40%.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
          <div className="md:col-span-4">
            <label className="block text-xs font-medium text-subtle mb-1.5">Dividendes Annuels Bruts (€)</label>
            <Input
              type="number"
              min="0"
              value={data.dividendesBruts || ''}
              onChange={(e) => handleFieldChange("dividendesBruts", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
              className="text-base font-bold"
            />

            <div className="mt-4">
              <label className="block text-xs font-medium text-subtle mb-1.5">Option retenue pour la simulation :</label>
              <CustomSelect
                value={data.optionDividendes}
                onChange={(val) => handleFieldChange("optionDividendes", val)}
                options={[
                  { value: "pfu", label: "Flat Tax (PFU 30%)" },
                  { value: "bareme", label: "Barème Progressif (Abattement 40%)" }
                ]}
              />
            </div>
          </div>

          {/* Encadré d'arbitrage comparatif */}
          <div className="md:col-span-8 p-4 rounded-xl bg-surface-muted/30 border border-workspace">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-subtle">Analyse d'Arbitrage</span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-black ${calculations.recommandationRCM === 'PFU' ? 'bg-info-100 text-info-700' : 'bg-success-100 text-success-700'}`}>
                Recommandation : {calculations.recommandationRCM === 'PFU' ? "Prélèvement Forfaitaire Unique (30%)" : "Option Barème Progressif"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className={`p-3 rounded-lg border ${data.optionDividendes === 'pfu' ? 'border-secondary bg-surface shadow-sm' : 'border-workspace bg-surface/50'}`}>
                <p className="font-bold text-primary mb-1">Option 1 : Flat Tax (PFU 30%)</p>
                <p className="text-subtle">12.8% IR + 17.2% Prélèvements Sociaux</p>
                <p className="text-base font-black text-primary mt-2">{formatEuro(calculations.coutPFU)}</p>
              </div>

              <div className={`p-3 rounded-lg border ${data.optionDividendes === 'bareme' ? 'border-secondary bg-surface shadow-sm' : 'border-workspace bg-surface/50'}`}>
                <p className="font-bold text-primary mb-1">Option 2 : Barème Progressif</p>
                <p className="text-subtle">Abattement 40% + CSG 6.8% déductible</p>
                <p className="text-base font-black text-primary mt-2">{formatEuro(calculations.coutBareme)}</p>
              </div>
            </div>

            {calculations.economieRCM > 0 && data.dividendesBruts > 0 && (
              <p className="mt-3 text-xs font-medium text-success-600 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                L'option {calculations.recommandationRCM} permet d'économiser <strong>{formatEuro(calculations.economieRCM)}</strong> d'impôt global.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

